import apiClient, { handleApiResponse, handleApiError } from './apiClient';

/**
 * Modern Master Data Service
 * 
 * This service handles all master data operations using modern REST endpoints.
 * It includes response transformation for legacy compatibility.
 */

class MasterDataService {
    constructor() {
        this.baseUrl = '/master-data/tables';
    }

    // ============================================================================
    // MASTER DATA CRUD OPERATIONS
    // ============================================================================

    /**
     * Get paginated list of master data
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} Transformed master data list response
     */
    async getMasterData(params = {}) {
        try {
            const response = await apiClient.get(this.baseUrl, { params });
            
            // Return the full response data structure to maintain pagination info
            if (response.data && typeof response.data === 'object') {
                return {
                    success: true,
                    data: response.data,  // Keep the full response structure
                    message: response.data.message || "Master data fetched successfully"
                };
            } else {
                // Fallback for legacy format
                return {
                    success: true,
                    data: Array.isArray(response.data) ? response.data : [],
                    total: Array.isArray(response.data) ? response.data.length : 0,
                    message: "Master data fetched successfully"
                };
            }
        } catch (error) {
            console.error('Error fetching master data:', error);
            return {
                success: false,
                data: [],
                total: 0,
                message: error.response?.data?.detail || "Failed to fetch master data"
            };
        }
    }

    /**
     * Get single master data item by ID
     * @param {string} masterDataId - Master data ID
     * @returns {Promise<Object>} Transformed master data response
     */
    async getMasterDataById(masterDataId) {
        try {
            const response = await apiClient.get(`${this.baseUrl}/${masterDataId}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching master data:', error);
            throw handleApiError(error);
        }
    }

    /**
     * Create new master data item
     * @param {Object} masterData - Master data in legacy format
     * @returns {Promise<Object>} Transformed master data response
     */
    async createMasterData(masterData) {
        try {
            const response = await apiClient.post(this.baseUrl, masterData);
            return response.data;
        } catch (error) {
            console.error('Error creating master data:', error);
            throw handleApiError(error);
        }
    }

    /**
     * Update existing master data item
     * @param {string} masterDataId - Master data ID
     * @param {Object} masterData - Updated master data in legacy format
     * @returns {Promise<Object>} Transformed master data response
     */
    async updateMasterData(masterDataId, masterData) {
        try {
            const response = await apiClient.put(`${this.baseUrl}/${masterDataId}`, masterData);
            return response.data;
        } catch (error) {
            console.error('Error updating master data:', error);
            throw handleApiError(error);
        }
    }

    /**
     * Delete master data item
     * @param {string} masterDataId - Master data ID
     * @returns {Promise<Object>} Success response
     */
    async deleteMasterData(masterDataId) {
        try {
            await apiClient.delete(`${this.baseUrl}/${masterDataId}`);
            return { success: true, message: "Master data deleted successfully" };
        } catch (error) {
            console.error('Error deleting master data:', error);
            throw handleApiError(error);
        }
    }

    /**
     * Bulk delete master data items
     * @param {Array<string>} masterDataIds - Array of master data IDs
     * @returns {Promise<Object>} Success response
     */
    async bulkDeleteMasterData(masterDataIds) {
        try {
            const response = await apiClient.delete(`${this.baseUrl}/bulk`, {
                data: { master_data_ids: masterDataIds }
            });
            return response.data;
        } catch (error) {
            console.error('Error bulk deleting master data:', error);
            throw handleApiError(error);
        }
    }

    // ============================================================================
    // MASTER DATA FILTERING & SEARCH
    // ============================================================================

    /**
     * Get master data by type
     * @param {string} dataType - Data type
     * @param {Object} params - Additional query parameters
     * @returns {Promise<Object>} Transformed master data list response
     */
    async getMasterDataByType(dataType, params = {}) {
        try {
            const response = await apiClient.get(this.baseUrl, { params: { ...params, data_type: dataType } });
            return response.data;
        } catch (error) {
            console.error('Error fetching master data by type:', error);
            throw handleApiError(error);
        }
    }

    /**
     * Get master data by category
     * @param {string} category - Category
     * @param {Object} params - Additional query parameters
     * @returns {Promise<Object>} Transformed master data list response
     */
    async getMasterDataByCategory(category, params = {}) {
        try {
            const response = await apiClient.get(this.baseUrl, { params: { ...params, category } });
            return response.data;
        } catch (error) {
            console.error('Error fetching master data by category:', error);
            throw handleApiError(error);
        }
    }

    /**
     * Search master data with advanced filters
     * @param {Object} searchParams - Search parameters
     * @returns {Promise<Object>} Transformed master data list response
     */
    async searchMasterData(searchParams) {
        try {
            const response = await apiClient.get(`${this.baseUrl}/search`, { params: searchParams });
            return response.data;
        } catch (error) {
            console.error('Error searching master data:', error);
            throw handleApiError(error);
        }
    }

    // ============================================================================
    // MASTER DATA CATEGORIES & TYPES
    // ============================================================================

    /**
     * Get available data types
     * @returns {Promise<Object>} Data types response
     */
    async getDataTypes() {
        try {
            const response = await apiClient.get('/master-data/data-types');
            return response.data;
        } catch (error) {
            console.error('Error fetching data types:', error);
            throw handleApiError(error);
        }
    }

    /**
     * Get available categories
     * @returns {Promise<Object>} Categories response
     */
    async getCategories() {
        try {
            const response = await apiClient.get('/master-data/categories');
            return response.data;
        } catch (error) {
            console.error('Error fetching categories:', error);
            throw handleApiError(error);
        }
    }

    // ============================================================================
    // MASTER DATA IMPORT/EXPORT
    // ============================================================================

    /**
     * Export master data
     * @param {Object} params - Export parameters
     * @returns {Promise<Object>} Export response
     */
    async exportMasterData(params = {}) {
        try {
            const response = await apiClient.get(`${this.baseUrl}/export`, { 
                params: params,
                responseType: 'blob'
            });
            return response.data;
        } catch (error) {
            console.error('Error exporting master data:', error);
            throw handleApiError(error);
        }
    }

    /**
     * Import master data
     * @param {FormData} fileData - File data
     * @returns {Promise<Object>} Import response
     */
    async importMasterData(fileData) {
        try {
            const response = await apiClient.post(`${this.baseUrl}/import`, fileData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            return response.data;
        } catch (error) {
            console.error('Error importing master data:', error);
            throw handleApiError(error);
        }
    }

    // ============================================================================
    // MASTER DATA VALIDATION
    // ============================================================================

    /**
     * Validate master data
     * @param {Object} masterData - Master data to validate
     * @returns {Promise<Object>} Validation response
     */
    async validateMasterData(masterData) {
        try {
            const response = await apiClient.post(`${this.baseUrl}/validate`, masterData);
            return response.data;
        } catch (error) {
            console.error('Error validating master data:', error);
            throw handleApiError(error);
        }
    }

    // ============================================================================
    // SPECIFIC MASTER DATA TYPES
    // ============================================================================

    /**
     * Get CPT codes
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} CPT codes response
     */
    async getCPTCodes(params = {}) {
        try {
            const response = await apiClient.get(this.baseUrl, { params: { ...params, data_type: 'cpt_code' } });
            return response.data;
        } catch (error) {
            console.error('Error fetching CPT codes:', error);
            throw handleApiError(error);
        }
    }

    /**
     * Get ICD-10 codes
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} ICD-10 codes response
     */
    async getICD10Codes(params = {}) {
        try {
            const response = await apiClient.get(this.baseUrl, { params: { ...params, data_type: 'icd10_code' } });
            return response.data;
        } catch (error) {
            console.error('Error fetching ICD-10 codes:', error);
            throw handleApiError(error);
        }
    }

    /**
     * Get HCPCS codes
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} HCPCS codes response
     */
    async getHCPCSCodes(params = {}) {
        try {
            const response = await apiClient.get(this.baseUrl, { params: { ...params, data_type: 'hcpcs_code' } });
            return response.data;
        } catch (error) {
            console.error('Error fetching HCPCS codes:', error);
            throw handleApiError(error);
        }
    }

    /**
     * Get drug master data
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>} Drug master data response
     */
    async getDrugMasterData(params = {}) {
        try {
            const response = await apiClient.get(this.baseUrl, { params: { ...params, data_type: 'drug' } });
            return response.data;
        } catch (error) {
            console.error('Error fetching drug master data:', error);
            throw handleApiError(error);
        }
    }

    // ============================================================================
    // LEGACY COMPATIBILITY METHODS
    // ============================================================================

    /**
     * Legacy method: Get master list
     * @param {Object} params - Legacy parameters
     * @returns {Promise<Object>} Legacy formatted response
     */
    async getMasterListAction(params = {}) {
        return this.getMasterData(params);
    }

    /**
     * Legacy method: Save master data (create or update)
     * @param {Object} masterData - Master data
     * @returns {Promise<Object>} Legacy formatted response
     */
    async saveMasterDataAction(masterData) {
        if (masterData.id) {
            return this.updateMasterData(masterData.id, masterData);
        } else {
            return this.createMasterData(masterData);
        }
    }

    /**
     * Legacy method: Delete master data
     * @param {string} masterDataId - Master data ID
     * @returns {Promise<Object>} Legacy formatted response
     */
    async deleteMasterDataAction(masterDataId) {
        return this.deleteMasterData(masterDataId);
    }

    /**
     * Legacy method: Get master data list by type
     * @param {string} type - Data type
     * @returns {Promise<Object>} Legacy formatted response
     */
    async getMasterDataListByTypeAction(type) {
        return this.getMasterDataByType(type);
    }

    /**
     * Legacy method: Get CPT master
     * @param {Object} params - Parameters
     * @returns {Promise<Object>} Legacy formatted response
     */
    async getCPTMasterAction(params = {}) {
        return this.getCPTCodes(params);
    }

    /**
     * Legacy method: Get ICD-10 master
     * @param {Object} params - Parameters
     * @returns {Promise<Object>} Legacy formatted response
     */
    async getICD10MasterAction(params = {}) {
        return this.getICD10Codes(params);
    }

    /**
     * Legacy method: Get HCPCS master
     * @param {Object} params - Parameters
     * @returns {Promise<Object>} Legacy formatted response
     */
    async getHCPCSMasterAction(params = {}) {
        return this.getHCPCSCodes(params);
    }

    /**
     * Legacy method: Get drug master
     * @param {Object} params - Parameters
     * @returns {Promise<Object>} Legacy formatted response
     */
    async getDrugMasterAction(params = {}) {
        return this.getDrugMasterData(params);
    }

    /**
     * Legacy method: View master data list
     * @param {Object} params - Parameters
     * @returns {Promise<Object>} Legacy formatted response
     */
    async viewMasterDataListAction(params = {}) {
        return this.getMasterData(params);
    }

    /**
     * Legacy method: Add master data
     * @param {Object} masterData - Master data
     * @returns {Promise<Object>} Legacy formatted response
     */
    async addMasterDataAction(masterData) {
        return this.createMasterData(masterData);
    }

    /**
     * Legacy method: Edit master data
     * @param {string} masterDataId - Master data ID
     * @param {Object} masterData - Master data
     * @returns {Promise<Object>} Legacy formatted response
     */
    async editMasterDataAction(masterDataId, masterData) {
        return this.updateMasterData(masterDataId, masterData);
    }

    // =========================================================================
    // MASTER DATA INTEGRATION METHODS FOR DYNAMIC FORMS
    // =========================================================================

    /**
     * Get column options from a master data table
     * @param {string} tableId - Master data table ID
     * @param {string} columnId - Column ID
     * @returns {Promise} - Promise resolving to column options
     */
    async getColumnOptions(tableId, columnId) {
        try {
            const response = await apiClient.get(
                `/master-data/tables/${tableId}/columns/${columnId}/options`
            );
            return response.data;
        } catch (error) {
            console.error('Error fetching column options:', error);
            throw error;
        }
    }

    /**
     * Get field options for a form field (supports both static and master data)
     * @param {string} formId - Form ID
     * @param {string} fieldId - Field ID
     * @returns {Promise} - Promise resolving to field options
     */
    async getFormFieldOptions(formId, fieldId) {
        try {
            const response = await apiClient.get(
                `/dynamic-forms/forms/${formId}/fields/${fieldId}/options`
            );
            return response.data;
        } catch (error) {
            console.error('Error fetching form field options:', error);
            throw error;
        }
    }

    /**
     * Get table records with pagination
     * @param {string} tableId - Master data table ID
     * @param {Object} params - Query parameters (page, pageSize)
     * @returns {Promise} - Promise resolving to table records
     */
    async getTableRecords(tableId, params = {}) {
        try {
            const response = await apiClient.get(
                `/master-data/tables/${tableId}/records`,
                { params }
            );
            return response.data;
        } catch (error) {
            console.error('Error fetching table records:', error);
            throw error;
        }
    }
}

// Export singleton instance
export default new MasterDataService(); 